// Mehrere Ziele je Stufe im Wizard: Auflösung der Space-Karte gegen die Ziele der Pipeline-Übersicht (Vertrag V.2).
// Der Space-Lader kennt das Standardziel nicht und setzt bei mehreren Instanzen einer Stufe `instanceId: null`
// samt `candidates[]`. Erst die Übersicht (stageTargets) benennt das Standardziel.
import { targetKeyOf } from "../../../utils/stageTargets";

/** Standardziel einer Stufe, sonst das erste Ziel. */
export const defaultTargetOf = rows => (rows || []).find(row => row.isDefault) || (rows || [])[0] || null;

/**
 * Setzt `instanceId`, `inst` der Stufen mit `candidates` und `qasInstance` aus dem Standardziel (Vertrag V.12).
 * Vor dem ersten erfolgreichen Lesen der Ziele (`fallbackFirst` aus) gibt es keine Erstwahl: `instanceId` bleibt leer.
 * Nach dem Lesen ohne Mehrzielmodus gilt der erste Kandidat (Verhalten wie bisher).
 * Gibt `space` selbst zurück, wenn nichts zu tun ist.
 */
export function applyStageTargets(space, stageTargets, { fallbackFirst = false } = {}) {
  if (!space || !Array.isArray(space.stages) || !space.stages.some(stage => stage.candidates?.length)) return space;
  let qas = space.qasInstance;
  const stages = space.stages.map(stage => {
    if (!stage.candidates?.length) return stage;
    const target = defaultTargetOf(stageTargets?.[stage.stageKey]);
    const pick = stage.candidates.find(candidate => candidate.id === target?.instanceId) || (fallbackFirst ? stage.candidates[0] : null);
    if (!pick) { if (stage.stageKey === space.zielStufe) qas = null; return stage.instanceId === null ? stage : { ...stage, instanceId: null }; }
    if (stage.stageKey === space.zielStufe) qas = pick;
    return stage.instanceId === pick.id && stage.inst === pick.name ? stage : { ...stage, instanceId: pick.id, inst: pick.name };
  });
  return { ...space, stages, qasInstance: qas };
}

/** Auswahl je Stufe, gültig gegen die Ziele: nur bekannte IDs, mindestens ein Ziel (Standardziel), DEV nie. */
export function normalizeSelection(stageTargets, selection, baseStage = null) {
  const out = {};
  for (const [stage, rows] of Object.entries(stageTargets || {})) {
    if (stage === "dev" || !Array.isArray(rows) || !rows.length) continue;
    const known = rows.map(row => row.targetId);
    const kept = (selection?.[stage] || []).filter(id => known.includes(id));
    out[stage] = kept.length ? known.filter(id => kept.includes(id)) : [defaultTargetOf(rows).targetId];
    // Die Basisspalte (Standardziel der ersten Vorwärtsstufe) ist nie abwählbar (Vertrag V.16).
    if (stage === baseStage && !out[stage].includes(defaultTargetOf(rows).targetId)) out[stage] = known.filter(id => id === defaultTargetOf(rows).targetId || out[stage].includes(id));
  }
  return out;
}

/** Spalten `{ key: targetKey, stageKey, targetId, code, name, instanceId, region, isDefault }` der gewählten Ziele. */
export function targetColumns(stages, stageTargets, selection) {
  return stages.flatMap(stage => {
    const rows = stageTargets?.[stage.key];
    if (!rows?.length) return [{ ...stage, stageKey: stage.key, targetId: "default", isDefault: true }];
    const chosen = selection?.[stage.key] || [defaultTargetOf(rows).targetId];
    return rows.filter(row => chosen.includes(row.targetId)).map(row => ({
      key: targetKeyOf(stage.key, row.targetId), stageKey: stage.key, targetId: row.targetId, code: stage.code,
      name: row.instanceName || stage.name, instanceId: row.instanceId, region: row.region ?? null, isDefault: row.isDefault === true }));
  });
}

/** Alle Ziele der Vorwärtsstufen als Spalten, unabhängig von der Wizard Auswahl (Payload und Kette, Vertrag V.10/V.11). */
export function allTargetColumns(stages, stageTargets) {
  const all = Object.fromEntries(Object.entries(stageTargets || {}).map(([stage, rows]) => [stage, rows.map(row => row.targetId)]));
  return targetColumns(stages, stageTargets, all);
}

/** Vorwärtsstufen im Format der Erbkette: `[{ stage, targets: [{ targetId, isDefault }] }]`. */
export function forwardTargets(stages, stageTargets) {
  return stages.map(stage => ({ stage: stage.key, targets: stageTargets?.[stage.key]?.length
    ? stageTargets[stage.key].map(row => ({ targetId: row.targetId, isDefault: row.isDefault === true })) : [{ targetId: "default", isDefault: true }] }));
}
